/*----------------------------------
- HOME SCREENER BLUEPRINT
----------------------------------*/

/*----------------------------------
- MEDIA VARIANTS
----------------------------------*/

// One film, two delivered cuts of the same 858-frame render. `desktop` is the
// landscape master the homepage VideoObject describes. `mobile` is the portrait
// recomposition shown below `mobileMediaQuery`, where the 16:9 desktop
// workspace shrinks to roughly 343px wide and stops being readable.
//
// Variants stay keyed instead of flattened into `mobileWebmPath` style fields
// because the component and the hook select one whole set at a time: poster
// pair, source pair, and reserved geometry. Mixing fields across cuts would
// reserve the wrong box and paint a landscape first frame in front of a
// portrait source.
//
// Poster and video dimensions stay separate facts inside a variant even while
// they agree. They describe different artifacts, and the delivered video size
// is a consequence of the transfer budget: the 72-second film had to ship at
// 1280 by 720 to stay inside it, while this 29-second one fits at its authored
// size with room to spare. Keeping the pairs apart means the next long cut can
// be downscaled again without reshaping the contract. Inside one variant both
// pairs must share one aspect ratio, since the figure reserves its geometry
// from that ratio.
const MEDIA_VARIANTS = {
    desktop: {
        posterWebpPath:
            '/public/assets/home/screener-blueprint/unique-domains-screener-blueprint-poster.webp',
        posterPngPath:
            '/public/assets/home/screener-blueprint/unique-domains-screener-blueprint-poster.png',
        webmPath: '/public/assets/home/screener-blueprint/unique-domains-screener-blueprint.webm',
        mp4Path: '/public/assets/home/screener-blueprint/unique-domains-screener-blueprint.mp4',
        posterWidth: 1920,
        posterHeight: 1080,
        videoWidth: 1920,
        videoHeight: 1080,
    },
    mobile: {
        posterWebpPath:
            '/public/assets/home/screener-blueprint/unique-domains-screener-blueprint-mobile-poster.webp',
        posterPngPath:
            '/public/assets/home/screener-blueprint/unique-domains-screener-blueprint-mobile-poster.png',
        webmPath:
            '/public/assets/home/screener-blueprint/unique-domains-screener-blueprint-mobile.webm',
        mp4Path:
            '/public/assets/home/screener-blueprint/unique-domains-screener-blueprint-mobile.mp4',
        posterWidth: 720,
        posterHeight: 1280,
        videoWidth: 720,
        videoHeight: 1280,
    },
} as const;

export type HomeScreenerBlueprintVariant = keyof typeof MEDIA_VARIANTS;

/*----------------------------------
- CATALOG
----------------------------------*/

export const HOME_SCREENER_BLUEPRINT = {
    id: 'unique-domains-screener-blueprint',
    // The film was cut before Portfolio was deleted (ADR-0081) and still closes on its
    // screen; the copy stops at the watchlist so no structured data sells a retired surface.
    // Re-cut the film to drop that scene and the desktop rail described below.
    name: 'The Unique Domains workflow, from morning briefing to watchlist',
    description:
        'An illustrative 29-second product motion moving through the Unique Domains workflow: the home briefing with its overnight radar run, plain-language Search that builds its own filters, a scored verdict with the signals behind it, an Analyse table comparing three names across appraisal sources, a client brief scored into a shortlist, a radar running daily, and a watchlist.',
    // One `alt` serves both posters because `<picture>` art direction carries a
    // single `<img>`, and rendering one figure per cut would make Chrome fetch
    // both poster pairs. It therefore describes only what both frames actually
    // show: the desktop cut additionally carries the left navigation, the
    // describe-a-name prompt, and the portfolio and renewals rail, while the
    // portrait cut carries section chips and a bottom tab bar instead.
    alt:
        'Blueprint view of the Unique Domains home briefing showing a morning greeting, an overnight radar run that matched nine names with three asks from $1,240 to $4,200, a domaining news brief on .com wholesale pricing, and a Q3 shortlist watchlist item whose auction ends tonight.',
    caption:
        'Illustrative product motion: a 29-second walkthrough of the Unique Domains workflow, connecting the home briefing, Search, Verify, Analyse, Radars, and Watchlists in one decision workflow.',
    // `homeScreenerBlueprintMotion.css` owns the same 639px boundary for the
    // reserved geometry and the poster it paints. A stylesheet cannot read this
    // module, so the media contract test asserts both spellings stay in step.
    mobileMediaQuery: '(max-width: 639px)',
    // Both cuts are 28.6s exactly (858 frames at 30fps), declared here rounded
    // to the nearest second. The media contract test compares this against each
    // shipped MP4's movie header at the same rounding. `uploadDate` describes
    // the film, not a delivery variant, so the portrait cut does not restamp it.
    duration: 'PT29S',
    uploadDate: '2026-08-05',
    variants: MEDIA_VARIANTS,
} as const;
